import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, Tag } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/utils/seo";
import Marquee from "@/components/Marquee";
import { PROJECTS } from "@/data/references";

export default function Referenzen() {
  const visible = PROJECTS;
  usePageMeta({
    title: "Referenzen & Projekte | HRG Service GmbH Mühlau AG",
    description: "Referenzprojekte der HRG Service GmbH: Bauendreinigung, Gebäudeunterhalt und Reinigung in den Kantonen Aargau, Luzern, Zürich und Zug.",
    path: "/referenzen",
  });

  return (
    <main className="pt-20">
      <section className="bg-mesh border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
          <p className="font-tech text-xs uppercase tracking-[0.3em] text-[#3B62A5] font-semibold mb-6 flex items-center gap-3">
            <span className="inline-block h-[2px] w-10 bg-[#3B62A5]" /> Referenzen
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0A111E] leading-[1.08]">
            <MaskedLines lines={[<>Objekte, die</>, <span className="text-[#3B62A5]">für uns sprechen.</span>]} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
              Aktuelle Projekte aus der Region — weitere Referenzen und Bilder folgen in Kürze.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/referenzen/${p.slug}`}
                  data-testid={`reference-card-${p.id}`}
                  className="glass-card group block w-full overflow-hidden rounded-3xl transition-[box-shadow,border-color] duration-300 hover:border-[#3B62A5]/40 hover:shadow-[0_20px_50px_rgba(59,98,165,0.15)]"
                >
                  <div className="relative overflow-hidden">
                    {p.img ? (
                      <img src={p.img} alt={p.title} loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                    ) : (
                      <div className="bg-blueprint flex aspect-[4/3] w-full items-center justify-center border-b border-dashed border-[#3B62A5]/40 bg-[#F8FAFC]">
                        <span className="font-tech text-xs uppercase tracking-[0.25em] text-[#3B62A5]/60">Bild & Details folgen</span>
                      </div>
                    )}
                    <span className="absolute left-4 top-4 bg-[#0A111E]/75 px-3 py-1 font-tech text-[10px] uppercase tracking-[0.2em] text-white">{p.category}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold tracking-tight text-[#0A111E]">{p.title}</h3>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5 text-[#3B62A5]" />{p.location}</p>
                    <p className="mt-3 flex items-center gap-1.5 text-sm text-[#3B62A5] font-medium"><Tag className="h-3.5 w-3.5" />{p.service}</p>
                    <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#0A111E] transition-colors duration-200 group-hover:text-[#3B62A5]">
                      Projekt ansehen
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </p>
                  </div>
                </Link>
              </motion.article>
            ))}
          </AnimatePresence>
          <div data-testid="references-more-placeholder" className="glass-card flex min-h-[320px] flex-col items-center justify-center rounded-3xl border-dashed p-8 text-center" style={{ borderStyle: "dashed" }}>
            <span className="font-tech text-xs uppercase tracking-[0.25em] text-[#3B62A5]">Bald verfügbar</span>
            <p className="mt-3 text-lg font-semibold tracking-tight text-[#0A111E]">Weitere Referenzen folgen</p>
            <p className="mt-2 max-w-xs text-sm text-slate-500 leading-relaxed">Wir arbeiten laufend an neuen Projekten — schauen Sie bald wieder vorbei.</p>
          </div>
        </motion.div>

        <Reveal delay={0.1} className="mt-16">
          <p className="text-xs text-slate-400 font-tech uppercase tracking-[0.2em]">* Weitere Projekte und Bilder folgen in Kürze — gerne informieren wir Sie auf Anfrage.</p>
        </Reveal>
      </section>

    </main>
  );
}
